import { useCallback, useState } from 'react';
import { FollowableUser } from 'js/social/shared/types';
import { captureMessage, withScope } from '@sentry/browser';

type FollowableUserById = {
  [id: number]: FollowableUser;
};

const DEFAULT_USER: FollowableUser = {
  id: -1,
  firstName: 'Unknown',
  middleInitials: null,
  lastName: 'User',
  displayName: 'Unknown User',
  pageName: 'UnknownUser',
  domainName: 'independent',
  createdAt: new Date().toISOString(),
  url: window.location.origin,
  photo: '',
  following: true,
  university: null,
  department: null,
  followers: 0,
};

/**
 * User state management for social news feed.
 * @param initialUsers The initial users to populate.
 */
const useUsers = (initialUsers?: FollowableUser[]) => {
  const [users, setUsers] = useState<FollowableUserById>(
    (initialUsers || []).reduce((prev, cur) => {
      prev[cur.id] = cur;
      return prev;
    }, {} as FollowableUserById),
  );

  const getUserData = useCallback(
    (id: number): FollowableUser => {
      if (users[id]) {
        return users[id];
      } else {
        withScope((scope) => {
          scope.setTag('team', 'wolf');
          scope.setExtra('user_id', id);
          captureMessage('Missing user information', 'error');
        });
        return DEFAULT_USER;
      }
    },
    [users],
  );

  const setUserFollowing = useCallback(
    (id: number, following: boolean) => {
      const data = users[id];
      if (data == null) {
        throw new Error(`missing user data for id ${id}`);
      } else {
        setUsers((currentUsers) => {
          const updatedUsers = { ...currentUsers };
          updatedUsers[id] = { ...data, following };
          return updatedUsers;
        });
      }
    },
    [users],
  );

  const setMoreUsers = useCallback((users: FollowableUser[]) => {
    setUsers((currentUsers) => {
      const updatedUsers = { ...currentUsers };
      users.forEach((u) => (updatedUsers[u.id] = u));
      return updatedUsers;
    });
  }, []);

  return {
    getUserData,
    setMoreUsers,
    setUserFollowing,
  };
};

export default useUsers;
